import { useTranslation } from 'react-i18next';

export function AchievementsSection() {
  const { t } = useTranslation();
  
  return (
    <section id="achievements" className="py-24 bg-gradient-to-b from-gray-50 to-white">
      <div className="container mx-auto px-6">
        <div className="text-center mb-16">
          <h2 className="text-4xl md:text-5xl font-bold text-gray-900 mb-6">
            {t('achievements_title')}<span className="text-blue-600">{t('achievements_title_highlight')}</span>
          </h2>
          <p className="text-xl text-gray-600 max-w-3xl mx-auto">
            {t('achievements_subtitle')}
          </p>
        </div>

        {/* Main Stats */}
        <div className="grid grid-cols-1 md:grid-cols-2 gap-8 mb-16">
          <div className="bg-gradient-to-br from-blue-600 to-blue-700 rounded-2xl p-12 text-white relative overflow-hidden">
            <div className="absolute top-0 right-0 w-64 h-64 bg-white/10 rounded-full -mr-32 -mt-32"></div>
            <div className="relative z-10">
              <div className="w-16 h-16 flex items-center justify-center bg-white/20 rounded-xl mb-6">
                <i className="ri-line-chart-line text-4xl"></i>
              </div>
              <h3 className="text-6xl md:text-7xl font-bold mb-4">{t('achievements_last_year')}</h3>
              <p className="text-2xl text-blue-100 mb-2">{t('achievements_last_year_label')}</p>
              <p className="text-blue-100">{t('achievements_last_year_period')}</p>
            </div>
          </div>

          <div className="bg-gradient-to-br from-green-600 to-green-700 rounded-2xl p-12 text-white relative overflow-hidden">
            <div className="absolute top-0 right-0 w-64 h-64 bg-white/10 rounded-full -mr-32 -mt-32"></div>
            <div className="relative z-10">
              <div className="w-16 h-16 flex items-center justify-center bg-white/20 rounded-xl mb-6">
                <i className="ri-rocket-line text-4xl"></i>
              </div>
              <h3 className="text-6xl md:text-7xl font-bold mb-4">{t('achievements_this_year')}</h3>
              <p className="text-2xl text-green-100 mb-2">{t('achievements_this_year_label')}</p>
              <p className="text-green-100">{t('achievements_this_year_period')}</p>
            </div>
          </div>
        </div>

        {/* Growth Indicator */}
        <div className="bg-white rounded-2xl p-12 shadow-xl border border-gray-100">
          <div className="flex flex-col md:flex-row items-center justify-between gap-8">
            <div className="flex-1">
              <h3 className="text-3xl font-bold text-gray-900 mb-4">{t('achievements_growth_title')}</h3>
              <p className="text-gray-600 text-lg mb-6">
                {t('achievements_growth_desc_1')}<strong className="text-green-600">{t('achievements_growth_rate')}</strong>{t('achievements_growth_desc_2')}
              </p>
              <div className="flex items-center gap-4">
                <div className="flex items-center gap-2">
                  <div className="w-4 h-4 bg-blue-600 rounded"></div>
                  <span className="text-gray-600">{t('achievements_year_2023')}</span>
                </div>
                <div className="flex items-center gap-2">
                  <div className="w-4 h-4 bg-green-600 rounded"></div>
                  <span className="text-gray-600">{t('achievements_year_2024')}</span>
                </div>
              </div>
            </div>
            
            <div className="flex-1">
              <div className="relative h-64">
                <div className="absolute bottom-0 left-0 right-0 flex items-end justify-around gap-8">
                  <div className="flex-1 bg-blue-600 rounded-t-xl relative" style={{ height: '40%' }}>
                    <div className="absolute -top-12 left-1/2 transform -translate-x-1/2 text-center">
                      <p className="text-3xl font-bold text-blue-600">{t('achievements_last_year')}</p>
                      <p className="text-sm text-gray-600">{t('achievements_year_2023')}</p>
                    </div>
                  </div>
                  <div className="flex-1 bg-green-600 rounded-t-xl relative" style={{ height: '100%' }}>
                    <div className="absolute -top-12 left-1/2 transform -translate-x-1/2 text-center">
                      <p className="text-3xl font-bold text-green-600">{t('achievements_this_year')}</p>
                      <p className="text-sm text-gray-600">{t('achievements_year_2024')}</p>
                    </div>
                    <div className="absolute top-4 left-1/2 transform -translate-x-1/2 bg-green-500 text-white px-3 py-1 rounded-full text-sm font-bold">
                      {t('achievements_growth_indicator')}
                    </div>
                  </div>
                </div>
              </div>
            </div>
          </div>
        </div>

        {/* Additional Stats */}
        <div className="grid grid-cols-2 md:grid-cols-4 gap-6 mt-16">
          <div className="text-center p-6 bg-blue-50 rounded-xl">
            <div className="w-12 h-12 flex items-center justify-center bg-blue-600 text-white rounded-lg mx-auto mb-4">
              <i className="ri-calendar-line text-2xl"></i>
            </div>
            <h4 className="text-3xl font-bold text-blue-600 mb-2">{t('achievements_stat_experience')}</h4>
            <p className="text-gray-600">{t('achievements_stat_experience_label')}</p>
          </div>
          
          <div className="text-center p-6 bg-blue-50 rounded-xl">
            <div className="w-12 h-12 flex items-center justify-center bg-blue-600 text-white rounded-lg mx-auto mb-4">
              <i className="ri-global-line text-2xl"></i>
            </div>
            <h4 className="text-3xl font-bold text-blue-600 mb-2">{t('achievements_stat_markets')}</h4>
            <p className="text-gray-600">{t('achievements_stat_markets_label')}</p>
          </div>
          
          <div className="text-center p-6 bg-blue-50 rounded-xl">
            <div className="w-12 h-12 flex items-center justify-center bg-blue-600 text-white rounded-lg mx-auto mb-4">
              <i className="ri-team-line text-2xl"></i>
            </div>
            <h4 className="text-3xl font-bold text-blue-600 mb-2">{t('achievements_stat_team')}</h4>
            <p className="text-gray-600">{t('achievements_stat_team_label')}</p>
          </div>
          
          <div className="text-center p-6 bg-blue-50 rounded-xl">
            <div className="w-12 h-12 flex items-center justify-center bg-blue-600 text-white rounded-lg mx-auto mb-4">
              <i className="ri-award-line text-2xl"></i>
            </div>
            <h4 className="text-3xl font-bold text-blue-600 mb-2">{t('achievements_stat_quality')}</h4>
            <p className="text-gray-600">{t('achievements_stat_quality_label')}</p>
          </div>
        </div>
      </div>
    </section>
  );
}
